import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Bike, Car, Truck, Zap, ArrowRight, MapPin, Check, BellRing, Search, ShieldCheck, X, Ticket, CheckCircle2, Sparkles, Gift } from "lucide-react";

const VEHICLES = [
  { id: "two_wheeler", label: "Two-Wheeler", icon: Bike, img: "https://images.pexels.com/photos/13328575/pexels-photo-13328575.jpeg" },
  { id: "three_wheeler", label: "Three-Wheeler", icon: Zap, img: "https://images.pexels.com/photos/16207910/pexels-photo-16207910.jpeg" },
  { id: "four_wheeler", label: "Four-Wheeler", icon: Car, img: "https://images.pexels.com/photos/16207910/pexels-photo-16207910.jpeg" },
  { id: "commercial", label: "Commercial", icon: Truck, img: "https://images.pexels.com/photos/12159230/pexels-photo-12159230.jpeg" },
];

const FUELS = ["petrol", "diesel", "cng", "lpg", "ev", "hybrid"];

export default function BookingPage() {
  const { partnerId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();

  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(partnerId || "");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    customer_address: "",
    vehicle_number: (sp.get("vehicle") || "").toUpperCase(),
    vehicle_type: sp.get("type") || "",
    fuel_type: sp.get("fuel") || "",
    slot_date: "",
    slot_time: "10:00",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [pendingNext, setPendingNext] = useState(null); // "dashboard" or "register"
  const [postBookingOpen, setPostBookingOpen] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [regPassword, setRegPassword] = useState("");
  const [saveVehicle, setSaveVehicle] = useState(true);
  const [postBookingTerms, setPostBookingTerms] = useState(false);

  // Centre-finder state
  const [centreQuery, setCentreQuery] = useState("");
  const [centreCity, setCentreCity] = useState("");
  const [centrePincode, setCentrePincode] = useState("");

  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedPartnerId) {
      api.get(`/partners/${selectedPartnerId}`).then((r) => setSelectedPartner(r.data)).catch(() => setSelectedPartner(null));
    }
  }, [selectedPartnerId]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, customer_name: user.name || f.customer_name, customer_email: user.email || f.customer_email }));
    }
  }, [user]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Enter a coupon code"); return; }
    setValidatingCoupon(true);
    try {
      const res = await api.post("/coupons/validate", { code_or_token: code });
      // Enforce vehicle scope on the client for immediate feedback
      const scope = res.data.service_scope || "any";
      if (form.vehicle_type && scope !== "any" && scope !== form.vehicle_type) {
        toast.error(`Coupon is valid only for ${scope.replace("_", " ")} vehicles`);
        setAppliedCoupon(null);
        return;
      }
      setAppliedCoupon(res.data);
      toast.success(`Voucher ${res.data.code} applied`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const fireCelebration = () => {
    // Two bursts from left and right, tuned for a delightful but short reveal
    const defaults = { spread: 70, ticks: 200, gravity: 0.9, decay: 0.94, startVelocity: 42, colors: ["#0891B2", "#22C55E", "#F59E0B", "#EC4899", "#8B5CF6"] };
    confetti({ ...defaults, particleCount: 90, origin: { x: 0.15, y: 0.65 }, angle: 60 });
    confetti({ ...defaults, particleCount: 90, origin: { x: 0.85, y: 0.65 }, angle: 120 });
    setTimeout(() => {
      confetti({ ...defaults, particleCount: 60, origin: { x: 0.5, y: 0.5 }, spread: 120, scalar: 0.9 });
    }, 300);
  };

  const continueFromCelebration = () => {
    setCelebrationOpen(false);
    if (pendingNext === "dashboard") {
      navigate("/dashboard/customer");
    } else {
      setPostBookingOpen(true);
    }
    setPendingNext(null);
  };

  const celebrationHeadline = appliedCoupon
    ? (appliedCoupon.discount_type === "free"
        ? "Complimentary PUC Unlocked!"
        : appliedCoupon.discount_type === "percent"
        ? `${appliedCoupon.discount_value}% Off Unlocked!`
        : `₹${appliedCoupon.discount_value} Off Unlocked!`)
    : "";

  const couponLabel = appliedCoupon
    ? (appliedCoupon.discount_type === "free"
        ? "Complimentary — full PUC covered"
        : appliedCoupon.discount_type === "percent"
        ? `${appliedCoupon.discount_value}% off the PUC fee`
        : `₹${appliedCoupon.discount_value} off the PUC fee`)
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPartnerId) { toast.error("Select a PUC centre"); return; }
    if (!form.vehicle_type) { toast.error("Select a vehicle type"); return; }
    if (!form.fuel_type) { toast.error("Select a fuel type"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, partner_id: selectedPartnerId };
      if (appliedCoupon?.code) payload.coupon_code = appliedCoupon.code;
      const res = await api.post("/bookings", payload);
      toast.success("Booking confirmed! Check your inbox / SMS.");
      setLastBooking(res.data);

      // Save the vehicle for logged-in customers (best-effort)
      if (user?.role === "customer") {
        try {
          await api.post("/vehicles", {
            vehicle_number: form.vehicle_number,
            vehicle_type: form.vehicle_type,
            fuel_type: form.fuel_type,
          });
        } catch { /* already exists — ignore */ }
      }

      // Celebrate if a voucher was applied — otherwise carry on with existing flow
      if (appliedCoupon?.code) {
        fireCelebration();
        setPendingNext(user?.role === "customer" ? "dashboard" : "register");
        setCelebrationOpen(true);
        return;
      }

      if (user?.role === "customer") {
        navigate("/dashboard/customer");
        return;
      }
      // Guest -> prompt to create an account for future reminders
      setPostBookingOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const finishRegistration = async () => {
    if (!regPassword || regPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (!postBookingTerms) { toast.error("Please accept the Terms & Privacy Policy"); return; }
    try {
      await register({
        name: form.customer_name,
        email: form.customer_email,
        phone: form.customer_phone,
        password: regPassword,
        role: "customer",
      });
      // Backend also claims automatically on register — this is a belt-and-braces call
      try { await api.post("/bookings/claim"); } catch { /* ignore */ }
      if (saveVehicle) {
        try {
          await api.post("/vehicles", {
            vehicle_number: form.vehicle_number,
            vehicle_type: form.vehicle_type,
            fuel_type: form.fuel_type,
          });
        } catch { /* ignore duplicate */ }
      }
      toast.success("Account created — we'll remind you before your PUC expires.");
      setPostBookingOpen(false);
      navigate("/dashboard/customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Book a PUC test</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-10">Three fields. One slot.</h1>

        <form onSubmit={submit} className="space-y-10">
          {/* Step 1: choose centre */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">01 — Choose centre</div>
              {selectedPartner && (
                <button
                  type="button"
                  data-testid="change-centre-btn"
                  onClick={() => { setSelectedPartnerId(""); setSelectedPartner(null); }}
                  className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors duration-200"
                >
                  <X className="w-3 h-3" /> Change
                </button>
              )}
            </div>

            {selectedPartner ? (
              <div data-testid="selected-centre" className="border-2 border-primary bg-primary/5 rounded-2xl p-6 shadow-md shadow-primary/10">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0"><Check className="w-5 h-5 text-primary" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-primary mb-1">Selected</div>
                      <div className="font-display text-xl font-bold tracking-tight">{selectedPartner.store_name}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {selectedPartner.address}, {selectedPartner.city} {selectedPartner.pincode}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(selectedPartner.services_offered || []).map((s) => (
                      <span key={s} className="text-xs bg-white border border-border px-2 py-1 rounded-full">{s.replace("_", " ")}</span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {/* Search bar */}
                <div className="grid md:grid-cols-[1.6fr_1fr_1fr] gap-3 mb-5">
                  <div className="relative">
                    <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
                    <Input
                      data-testid="centre-search"
                      value={centreQuery}
                      onChange={(e) => setCentreQuery(e.target.value)}
                      placeholder="Search by centre name or area"
                      className="rounded-full bg-white border-border h-12 pl-11"
                    />
                  </div>
                  <Input
                    data-testid="centre-city"
                    value={centreCity}
                    onChange={(e) => setCentreCity(e.target.value)}
                    placeholder="City"
                    className="rounded-full bg-white border-border h-12 px-5"
                  />
                  <Input
                    data-testid="centre-pincode"
                    value={centrePincode}
                    onChange={(e) => setCentrePincode(e.target.value)}
                    placeholder="Pincode"
                    className="rounded-full bg-white border-border h-12 px-5"
                  />
                </div>

                {/* Chips */}
                {(centreQuery || centreCity || centrePincode || form.vehicle_type) && (
                  <div className="flex flex-wrap gap-2 mb-4 items-center text-xs" data-testid="centre-filter-chips">
                    <span className="uppercase tracking-[0.15em] text-muted-foreground">Filtered by:</span>
                    {form.vehicle_type && <span className="bg-primary/10 text-primary border border-primary/30 px-3 py-1 rounded-full">Services {form.vehicle_type.replace("_", " ")}</span>}
                    {centreQuery && <span className="bg-secondary border border-border px-3 py-1 rounded-full">“{centreQuery}”</span>}
                    {centreCity && <span className="bg-secondary border border-border px-3 py-1 rounded-full">{centreCity}</span>}
                    {centrePincode && <span className="bg-secondary border border-border px-3 py-1 rounded-full">{centrePincode}</span>}
                    <button type="button" onClick={() => { setCentreQuery(""); setCentreCity(""); setCentrePincode(""); }} className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">clear</button>
                  </div>
                )}

                {/* Result grid */}
                {(() => {
                  const q = centreQuery.trim().toLowerCase();
                  const city = centreCity.trim().toLowerCase();
                  const pin = centrePincode.trim();
                  const filtered = partners.filter((p) => {
                    if (form.vehicle_type && p.services_offered && !p.services_offered.includes(form.vehicle_type)) return false;
                    if (city && !(p.city || "").toLowerCase().includes(city)) return false;
                    if (pin && !(p.pincode || "").startsWith(pin)) return false;
                    if (q) {
                      const hay = `${p.store_name} ${p.address} ${p.city}`.toLowerCase();
                      if (!hay.includes(q)) return false;
                    }
                    return true;
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="border border-dashed border-border rounded-2xl p-10 text-center" data-testid="centre-empty">
                        <div className="font-display text-lg font-bold mb-1">No matching centres</div>
                        <p className="text-sm text-muted-foreground">Try broadening your city, pincode, or search text.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid md:grid-cols-2 gap-3 max-h-[520px] overflow-auto pr-1" data-testid="centre-results">
                      {filtered.map((p) => (
                        <button
                          key={p.partner_id}
                          type="button"
                          data-testid={`centre-card-${p.partner_id}`}
                          onClick={() => setSelectedPartnerId(p.partner_id)}
                          className="text-left border border-border bg-white rounded-2xl p-5 hover:border-primary hover:shadow-lg hover:-translate-y-1 hover:shadow-primary/10 transition-all duration-300 group"
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                              <div className="font-display text-lg font-bold tracking-tight group-hover:text-primary transition-colors duration-200">{p.store_name}</div>
                              <div className="text-xs text-muted-foreground flex items-start gap-1.5 mt-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> {p.address}, {p.city} {p.pincode}</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0"><ShieldCheck className="w-3 h-3" /> RTO</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-4">
                            {(p.services_offered || []).slice(0, 4).map((s) => (
                              <span key={s} className="text-[11px] bg-secondary border border-border px-2 py-0.5 rounded-full">{s.replace("_", " ")}</span>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground uppercase tracking-[0.15em]">Tap to select</span>
                            <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform duration-200" />
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </section>

          {/* Step 2: vehicle type cards */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">02 — Vehicle type</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="vehicle-type-grid">
              {VEHICLES.map((v) => {
                const active = form.vehicle_type === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    data-testid={`vehicle-type-${v.id}`}
                    onClick={() => setForm({ ...form, vehicle_type: v.id })}
                    className={`group relative h-44 md:h-56 overflow-hidden text-left rounded-2xl border transition-all duration-300 ${active ? "border-primary ring-2 ring-primary/40 shadow-xl shadow-primary/20" : "border-border hover:border-primary hover:shadow-lg hover:-translate-y-1"}`}
                  >
                    <img src={v.img} alt="" className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${active ? "opacity-80 scale-105" : "opacity-40 group-hover:opacity-70 group-hover:scale-105"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/20" />
                    <div className="relative p-5 h-full flex flex-col justify-between">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${active ? "bg-primary text-primary-foreground" : "bg-white/90 backdrop-blur border border-border text-primary"}`}>
                        <v.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-display text-lg md:text-xl font-bold tracking-tight text-foreground">{v.label}</div>
                        {active && <div className="text-xs text-primary mt-1 flex items-center gap-1 font-semibold"><Check className="w-3 h-3" /> Selected</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 3: vehicle + customer details */}
          <section className="grid md:grid-cols-2 gap-6 border border-border bg-card p-6 md:p-8">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle number</Label>
              <Input data-testid="booking-vehicle-number" required value={form.vehicle_number} onChange={setField("vehicle_number")} className="rounded-lg bg-secondary border-border h-11 mt-2 font-mono uppercase" placeholder="MH12AB1234" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fuel type</Label>
              <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                <SelectTrigger data-testid="booking-fuel" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue placeholder="Select fuel" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {FUELS.map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Slot date</Label>
              <Input data-testid="booking-slot-date" type="date" required value={form.slot_date} onChange={setField("slot_date")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Slot time</Label>
              <Select value={form.slot_time} onValueChange={(v) => setForm({ ...form, slot_time: v })}>
                <SelectTrigger data-testid="booking-slot-time" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {["09:00","10:00","11:00","12:00","14:00","15:00","16:00","17:00","18:00"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 pt-4 border-t border-border">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Customer details</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label>
              <Input data-testid="booking-cust-name" required value={form.customer_name} onChange={setField("customer_name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
              <Input data-testid="booking-cust-phone" required value={form.customer_phone} onChange={setField("customer_phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
              <Input data-testid="booking-cust-email" type="email" required value={form.customer_email} onChange={setField("customer_email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
              <Input data-testid="booking-cust-address" required value={form.customer_address} onChange={setField("customer_address")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes (optional)</Label>
              <Textarea data-testid="booking-notes" value={form.notes} onChange={setField("notes")} className="rounded-lg bg-secondary border-border mt-2" placeholder="Any special requirements?" />
            </div>

            <div className="md:col-span-2 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-3">
                <Ticket className="w-3.5 h-3.5" /> Have a voucher?
              </div>
              {appliedCoupon ? (
                <div data-testid="applied-coupon" className="flex flex-wrap items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                    <div>
                      <div className="font-semibold text-emerald-900">Voucher applied · <span className="font-mono">{appliedCoupon.code}</span></div>
                      <div className="text-xs text-emerald-800">{couponLabel}</div>
                      <div className="text-[11px] text-emerald-700 mt-0.5">Show this code at the centre — partner will redeem after your PUC test.</div>
                    </div>
                  </div>
                  <Button data-testid="remove-coupon-btn" type="button" onClick={removeCoupon} variant="outline" size="sm" className="rounded-lg border-emerald-300 h-9">
                    <X className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    data-testid="booking-coupon-input"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="e.g. HM34GNND"
                    className="rounded-lg bg-secondary border-border h-11 font-mono flex-1"
                  />
                  <Button data-testid="apply-coupon-btn" type="button" onClick={applyCoupon} disabled={validatingCoupon || !couponInput.trim()} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary/5 h-11 sm:w-40">
                    {validatingCoupon ? "Checking…" : "Apply voucher"}
                  </Button>
                </div>
              )}
            </div>
          </section>

          <div className="flex flex-wrap gap-4 items-center">
            <Button data-testid="booking-submit-btn" type="submit" disabled={submitting} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-14 px-10 text-base">
              {submitting ? "Booking…" : "Confirm booking"} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            {!user && (
              <span className="text-xs text-muted-foreground uppercase tracking-[0.15em]">Not signed in — booking as guest</span>
            )}
          </div>
        </form>

        <Dialog open={celebrationOpen} onOpenChange={(o) => { if (!o) continueFromCelebration(); }}>
          <DialogContent data-testid="celebration-dialog" className="rounded-2xl border-primary/30 bg-gradient-to-br from-white via-emerald-50 to-cyan-50 max-w-md p-0 overflow-hidden">
            <div className="p-8 text-center">
              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 mb-4 shadow-lg shadow-emerald-500/30 animate-[bounce_1s_ease-in-out]">
                <Gift className="w-10 h-10 text-white" strokeWidth={2.2} />
                <Sparkles className="w-5 h-5 text-yellow-300 absolute -top-1 -right-1" />
                <Sparkles className="w-4 h-4 text-yellow-300 absolute -bottom-1 -left-1" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 font-semibold mb-2">🎉 A Little Gift for You</div>
              <h2 data-testid="celebration-headline" className="font-display text-3xl font-black tracking-tight text-foreground mb-3">
                {celebrationHeadline}
              </h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Your booking is confirmed <span className="text-emerald-700 font-semibold">and</span> the voucher is locked in.
                Show this code at the centre — your PUC test is on the house.
              </p>

              <div className="bg-white border border-primary/20 rounded-xl p-4 mb-6 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Voucher code</div>
                <div className="font-mono text-2xl font-bold text-primary tracking-widest" data-testid="celebration-code">{appliedCoupon?.code}</div>
                {lastBooking?.partner_snapshot?.store_name && (
                  <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                    Redeem at <b className="text-foreground">{lastBooking.partner_snapshot.store_name}</b>
                    {lastBooking.slot_date && <> on <b className="text-foreground">{lastBooking.slot_date} · {lastBooking.slot_time}</b></>}
                  </div>
                )}
              </div>

              <Button data-testid="celebration-continue-btn" onClick={continueFromCelebration} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
                {pendingNext === "dashboard" ? "Continue to my dashboard" : "Continue"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-muted-foreground mt-4">A confirmation with the code has been emailed & SMS'd to you.</p>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={postBookingOpen} onOpenChange={setPostBookingOpen}>
          <DialogContent data-testid="post-booking-dialog" className="rounded-lg border-border bg-card max-w-lg">
            <DialogHeader>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-2">
                <BellRing className="w-4 h-4" /> Never miss a renewal
              </div>
              <DialogTitle className="font-display text-2xl tracking-tight">Save your booking for future reminders</DialogTitle>
            </DialogHeader>

            <p className="text-sm text-muted-foreground">
              Create a free account and we&apos;ll auto-remind you 3 days before your PUC certificate for
              <span className="font-mono text-foreground"> {form.vehicle_number}</span> expires.
            </p>

            <div className="border border-border bg-secondary p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span>{form.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{form.customer_email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{form.customer_phone}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Vehicle</span><span className="font-mono">{form.vehicle_number} · {form.vehicle_type.replace("_"," ")} · {form.fuel_type}</span></div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Set a password</Label>
              <Input data-testid="post-booking-password" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
              <input type="checkbox" checked={saveVehicle} onChange={(e) => setSaveVehicle(e.target.checked)} data-testid="post-booking-save-vehicle" />
              Save this vehicle for one-click future bookings
            </label>

            <label className="flex items-start gap-3 text-sm text-muted-foreground select-none">
              <Checkbox
                data-testid="post-booking-accept-terms"
                checked={postBookingTerms}
                onCheckedChange={(v) => setPostBookingTerms(!!v)}
                className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span>
                I agree to VayuCheck&apos;s{" "}
                <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">Terms</Link>{" "}and{" "}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">Privacy Policy</Link>.
              </span>
            </label>

            <DialogFooter>
              <Button data-testid="post-booking-skip" variant="outline" onClick={() => { setPostBookingOpen(false); navigate("/"); }} className="rounded-lg border-border text-foreground hover:bg-secondary">Not now</Button>
              <Button data-testid="post-booking-register" onClick={finishRegistration} disabled={!postBookingTerms} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Create account</Button>
            </DialogFooter>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </p>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
